import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { NotificationPrefs, SessionInfo, User } from "@wiseowl/shared";
import { isApiError } from "@wiseowl/shared";
import { useAuth } from "../../lib/auth-context.js";
import { api } from "../../lib/api.js";
import { LOGO_SRC, SvgBack } from "./AuthShell.js";
import "./_auth.css";

type Tab = "profile" | "security" | "notifications" | "privacy";

/**
 * /profile — in-app settings.
 *
 * Four tabs (Profile / Security / Notifications / Privacy), each wired
 * to the live worker:
 *   - Profile: PATCH /me { displayName }
 *   - Security: POST /me/password, GET/POST /me/sessions, /me/sessions/:id/revoke
 *   - Notifications: PATCH /me { notifications }
 *   - Privacy: POST /me/export, DELETE /me, plus the dataImprove toggle
 *
 * Sign-out and the back-link to /home stay as-is. The page no longer
 * shows mocked stats; it shows what /api/me actually returns plus the
 * live session list.
 */
export const Profile = () => {
  const navigate = useNavigate();
  const { user, setUser, signOut } = useAuth();
  const [tab, setTab] = useState<Tab>("profile");

  const onSignOut = async () => {
    await signOut();
    navigate("/sign-in");
  };

  if (!user) return null;
  const joined = new Date(user.createdAt).toLocaleDateString("en-US", { month: "long", year: "numeric" });

  return (
    <div className="auth-page profile-page">
      <div className="pf-shell">
        <div className="pf-top">
          <Link className="lockup" to="/home">
            <div className="owl-avatar sm"><img src={LOGO_SRC} alt="" /></div>
            <div className="wordmark dark" style={{ fontSize: 17 }}>Wise<span>Owl</span> Talks</div>
          </Link>
          <Link className="pf-back" to="/home">{SvgBack} Back to today</Link>
        </div>

        <div className="pf-header">
          <div className="eyebrow">Your account</div>
          <h1>A small space, kept for you.</h1>
          <p className="sub">Everything here is private. Change what you&apos;d like, when you&apos;d like.</p>
        </div>

        <div className="avatar-up">
          <div className="av"><img src={LOGO_SRC} alt="Avatar" /></div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="name">{user.name}</div>
            <div className="email">{user.email} · joined {joined}</div>
          </div>
        </div>

        <div className="pf-tabs">
          <div className="tab-row" role="tablist">
            <TabBtn id="profile" active={tab} setTab={setTab}>Profile</TabBtn>
            <TabBtn id="security" active={tab} setTab={setTab}>Security</TabBtn>
            <TabBtn id="notifications" active={tab} setTab={setTab}>Notifications</TabBtn>
            <TabBtn id="privacy" active={tab} setTab={setTab}>Privacy</TabBtn>
          </div>
        </div>

        <div className={`pf-panel${tab === "profile" ? " active" : ""}`}>
          <ProfileTab user={user} onUpdated={setUser} />
        </div>
        <div className={`pf-panel${tab === "security" ? " active" : ""}`}>
          <SecurityTab />
        </div>
        <div className={`pf-panel${tab === "notifications" ? " active" : ""}`}>
          <NotificationsTab user={user} onUpdated={setUser} />
        </div>
        <div className={`pf-panel${tab === "privacy" ? " active" : ""}`}>
          <PrivacyTab user={user} onUpdated={setUser} onDeleted={onSignOut} />
        </div>

        <div style={{
          marginTop: 24, display: "flex", justifyContent: "space-between",
          alignItems: "center", gap: 14, flexWrap: "wrap",
        }}>
          <div style={{ fontSize: 12.5, color: "rgb(var(--wo-muted))", lineHeight: 1.4 }}>
            Signed in as <strong style={{ color: "rgb(var(--wo-ink))" }}>{user.email}</strong>
          </div>
          <button type="button" onClick={onSignOut} className="btn-ghost" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M14 4h5v16h-5M10 8l-4 4 4 4M6 12h12" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────── Profile tab ─────────────────────── */

const ProfileTab = ({ user, onUpdated }: { user: User; onUpdated: (u: User) => void }) => {
  const [name, setName] = useState(user.name);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ tone: "ok" | "err"; text: string } | null>(null);

  const onSave = async () => {
    const trimmed = name.trim();
    if (trimmed.length < 1) return setMsg({ tone: "err", text: "Name can't be empty." });
    setSaving(true); setMsg(null);
    const r = await api<{ user: User }>("/me", {
      method: "PATCH",
      body: JSON.stringify({ displayName: trimmed }),
    });
    setSaving(false);
    if (isApiError(r)) return setMsg({ tone: "err", text: r.error.message });
    onUpdated(r.data.user);
    setMsg({ tone: "ok", text: "Saved." });
  };

  return (
    <div className="profile-card">
      <div className="card-title">About you</div>
      <p className="card-sub">Only your name is used inside the app. We never show it elsewhere.</p>
      <div className="field">
        <label className="field-label" htmlFor="pf-name">Display name</label>
        <input id="pf-name" className="field-input"
          value={name} onChange={(e) => setName(e.target.value)} maxLength={80}
        />
      </div>
      {msg && <FieldNote tone={msg.tone}>{msg.text}</FieldNote>}
      <button type="button" className="btn-primary" style={{ marginTop: 8 }} onClick={onSave} disabled={saving}>
        {saving ? "Saving…" : "Save changes"}
      </button>
    </div>
  );
};

/* ─────────────────────── Security tab ─────────────────────── */

const SecurityTab = () => {
  return (
    <>
      <ChangePasswordCard />
      <SessionsCard />
    </>
  );
};

const ChangePasswordCard = () => {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ tone: "ok" | "err"; text: string } | null>(null);

  const onSave = async () => {
    if (!current || !next) return setMsg({ tone: "err", text: "Both fields are required." });
    if (next.length < 8) return setMsg({ tone: "err", text: "New password must be at least 8 characters." });
    setSaving(true); setMsg(null);
    const r = await api<{ ok: boolean; otherSessionsRevoked: number }>("/me/password", {
      method: "POST",
      body: JSON.stringify({ currentPassword: current, newPassword: next }),
    });
    setSaving(false);
    if (isApiError(r)) return setMsg({ tone: "err", text: r.error.message });
    setCurrent(""); setNext("");
    const note = r.data.otherSessionsRevoked > 0
      ? `Password updated. ${r.data.otherSessionsRevoked} other ${r.data.otherSessionsRevoked === 1 ? "session" : "sessions"} signed out.`
      : "Password updated.";
    setMsg({ tone: "ok", text: note });
  };

  return (
    <div className="profile-card">
      <div className="card-title">Change password</div>
      <p className="card-sub">For your safety, other signed-in devices will be signed out automatically.</p>
      <div className="field">
        <label className="field-label" htmlFor="pf-curpw">Current password</label>
        <input id="pf-curpw" className="field-input" type="password" autoComplete="current-password"
          value={current} onChange={(e) => setCurrent(e.target.value)} />
      </div>
      <div className="field">
        <label className="field-label" htmlFor="pf-newpw">New password</label>
        <input id="pf-newpw" className="field-input" type="password" autoComplete="new-password"
          value={next} onChange={(e) => setNext(e.target.value)} />
        <div className="field-help">At least 8 characters. A small phrase beats a clever scramble.</div>
      </div>
      {msg && <FieldNote tone={msg.tone}>{msg.text}</FieldNote>}
      <div className="btn-row" style={{ marginTop: 8 }}>
        <button type="button" className="btn-primary" onClick={onSave} disabled={saving}>
          {saving ? "Saving…" : "Update password"}
        </button>
        <Link to="/forgot" className="btn-ghost">Forgot current password?</Link>
      </div>
    </div>
  );
};

const SessionsCard = () => {
  const [sessions, setSessions] = useState<SessionInfo[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ tone: "ok" | "err"; text: string } | null>(null);

  const refresh = useCallback(async () => {
    const r = await api<{ sessions: SessionInfo[] }>("/me/sessions");
    if (!isApiError(r)) setSessions(r.data.sessions);
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  const revokeOne = async (id: string) => {
    setBusy(id); setMsg(null);
    const r = await api<{ ok: boolean }>(`/me/sessions/${id}/revoke`, { method: "POST" });
    setBusy(null);
    if (isApiError(r)) return setMsg({ tone: "err", text: r.error.message });
    setMsg({ tone: "ok", text: "Device signed out." });
    void refresh();
  };

  const revokeAll = async () => {
    setBusy("all"); setMsg(null);
    const r = await api<{ revoked: number }>("/me/sessions/revoke-all", { method: "POST" });
    setBusy(null);
    if (isApiError(r)) return setMsg({ tone: "err", text: r.error.message });
    setMsg({
      tone: "ok",
      text: r.data.revoked === 0 ? "Nothing else to sign out." : `Signed out of ${r.data.revoked} other ${r.data.revoked === 1 ? "device" : "devices"}.`,
    });
    void refresh();
  };

  return (
    <div className="profile-card">
      <div className="card-title">Devices</div>
      <p className="card-sub">Everywhere you&apos;re signed in. Revoke anything that looks unfamiliar.</p>
      {sessions === null && <p className="card-sub">Loading…</p>}
      {sessions?.length === 0 && <p className="card-sub">No active sessions yet.</p>}
      {sessions?.map((s) => (
        <div key={s.id} className="profile-row">
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="k">{s.current ? "This device" : "Another device"}</div>
            <div className="v" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {summariseUA(s.userAgent)}
            </div>
            <div className="meta">
              {s.ip ? `${s.ip} · ` : ""}last seen {timeAgo(s.lastSeenAt)}
            </div>
          </div>
          {s.current
            ? <span className="pill-success">Active</span>
            : <button type="button" className="action" disabled={busy === s.id} onClick={() => revokeOne(s.id)}>
                {busy === s.id ? "…" : "Sign out"}
              </button>
          }
        </div>
      ))}
      {msg && <FieldNote tone={msg.tone}>{msg.text}</FieldNote>}
      <button type="button" className="btn-secondary" style={{ marginTop: 14 }}
        disabled={busy === "all" || (sessions?.filter((s) => !s.current).length ?? 0) === 0}
        onClick={revokeAll}>
        {busy === "all" ? "Signing out…" : "Sign out of all other devices"}
      </button>
    </div>
  );
};

/* ─────────────────────── Notifications tab ─────────────────────── */

const NOTIFS: { k: keyof NotificationPrefs; title: string; meta: string }[] = [
  { k: "dailyNudge",  title: "Daily reset nudge",        meta: "A single quiet reminder at your chosen time." },
  { k: "streakNudge", title: "Streak gentle-reminder",   meta: "Only if you've missed two days in a row." },
  { k: "weeklyEmail", title: "Weekly reflection email",  meta: "Sundays. A summary of what you noticed." },
  { k: "insights",    title: "Insights we noticed", meta: "When a pattern shows up worth sharing." },
  { k: "productNews", title: "Product news",             meta: "Rarely — only when something meaningful changes." },
];

const NotificationsTab = ({ user, onUpdated }: { user: User; onUpdated: (u: User) => void }) => {
  const [busy, setBusy] = useState<keyof NotificationPrefs | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const flip = async (k: keyof NotificationPrefs) => {
    const next = !user.notifications[k];
    setBusy(k); setMsg(null);
    const r = await api<{ user: User }>("/me", {
      method: "PATCH",
      body: JSON.stringify({ notifications: { [k]: next } }),
    });
    setBusy(null);
    if (isApiError(r)) { setMsg(r.error.message); return; }
    onUpdated(r.data.user);
  };

  return (
    <div className="profile-card">
      <div className="card-title">Gentle reminders</div>
      <p className="card-sub">Choose what feels supportive — never noisy.</p>
      {NOTIFS.map(({ k, title, meta }) => (
        <NotifRow key={k} title={title} meta={meta}
          on={user.notifications[k]}
          busy={busy === k}
          onFlip={() => flip(k)}
        />
      ))}
      {msg && <FieldNote tone="err">{msg}</FieldNote>}
    </div>
  );
};

/* ─────────────────────── Privacy tab ─────────────────────── */

const PrivacyTab = ({
  user, onUpdated, onDeleted,
}: { user: User; onUpdated: (u: User) => void; onDeleted: () => void }) => {
  const [flipBusy, setFlipBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportMsg, setExportMsg] = useState<{ tone: "ok" | "err"; text: string } | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const flipImprove = async () => {
    setFlipBusy(true);
    const r = await api<{ user: User }>("/me", {
      method: "PATCH",
      body: JSON.stringify({ notifications: { dataImprove: !user.notifications.dataImprove } }),
    });
    setFlipBusy(false);
    if (!isApiError(r)) onUpdated(r.data.user);
  };

  const onExport = async () => {
    setExporting(true); setExportMsg(null);
    const r = await api<{
      delivered: boolean; sizeBytes: number; attachmentBase64?: string;
    }>("/me/export", { method: "POST" });
    setExporting(false);
    if (isApiError(r)) return setExportMsg({ tone: "err", text: r.error.message });
    if (r.data.delivered) {
      setExportMsg({ tone: "ok", text: `We just emailed ${user.email} a JSON copy (${formatBytes(r.data.sizeBytes)}).` });
    } else if (r.data.attachmentBase64) {
      // Email delivery couldn't reach the address (e.g. Resend test-mode).
      // Hand the data straight to the browser as a download.
      downloadJsonFromBase64(r.data.attachmentBase64, "wise-owl-export.json");
      setExportMsg({ tone: "ok", text: "Email delivery is paused for your address — your export downloaded locally instead." });
    }
  };

  return (
    <>
      <div className="profile-card">
        <div className="card-title">Your data</div>
        <p className="card-sub">Wise Owl Talks is your private space. Your reflections are never sold and never used to train external models.</p>

        <NotifRow title="Use my reflections to improve Wise Owl Talks"
          meta="Anonymized, opt-in. Off by default."
          on={user.notifications.dataImprove}
          busy={flipBusy} onFlip={flipImprove}
        />

        <div className="profile-row">
          <div style={{ flex: 1 }}>
            <div className="v">Export everything</div>
            <div className="meta">A JSON of your reflections, moods, enrollments — emailed to you.</div>
          </div>
          <button type="button" className="action" onClick={onExport} disabled={exporting}>
            {exporting ? "…" : "Download"}
          </button>
        </div>
        {exportMsg && <FieldNote tone={exportMsg.tone}>{exportMsg.text}</FieldNote>}
      </div>

      <div className="profile-card" style={{ borderColor: "rgb(180 69 44 / 0.25)" }}>
        <div className="card-title" style={{ color: "rgb(var(--wo-danger))" }}>Quiet exits</div>
        <p className="card-sub">Account deletion soft-removes your record and ends every session. Reflections are kept encrypted for 30 days, then purged.</p>
        <div className="btn-row" style={{ marginTop: 6 }}>
          <button type="button" className="btn-danger" onClick={() => setConfirmOpen(true)}>
            Delete account
          </button>
        </div>
        <p className="reflect" style={{ textAlign: "left", marginTop: 18 }}>
          &ldquo;Endings can be quiet too.&rdquo;
        </p>
      </div>

      {confirmOpen && (
        <DeleteAccountModal email={user.email} onCancel={() => setConfirmOpen(false)} onDeleted={onDeleted} />
      )}
    </>
  );
};

const DeleteAccountModal = ({
  email, onCancel, onDeleted,
}: { email: string; onCancel: () => void; onDeleted: () => void }) => {
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const onDelete = async () => {
    setBusy(true); setErr(null);
    const r = await api<{ ok: boolean }>("/me", {
      method: "DELETE",
      body: JSON.stringify({ confirmation: confirm }),
    });
    setBusy(false);
    if (isApiError(r)) return setErr(r.error.message);
    onDeleted();
  };

  return (
    <div onClick={onCancel} style={{
      position: "fixed", inset: 0, background: "rgba(20,20,20,0.55)",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 16, zIndex: 50,
    }}>
      <div onClick={(e) => e.stopPropagation()} className="profile-card" style={{
        background: "rgb(var(--wo-cream))", maxWidth: 460, width: "100%",
      }}>
        <div className="card-title" style={{ color: "rgb(var(--wo-danger))" }}>Delete account</div>
        <p className="card-sub">This signs you out of every device and removes your account record. Type your email below to confirm.</p>
        <div className="field">
          <input className="field-input" placeholder={email}
            value={confirm} onChange={(e) => setConfirm(e.target.value)} />
        </div>
        {err && <FieldNote tone="err">{err}</FieldNote>}
        <div className="btn-row" style={{ marginTop: 8 }}>
          <button type="button" className="btn-ghost" onClick={onCancel} disabled={busy}>Keep my account</button>
          <button type="button" className="btn-danger" onClick={onDelete}
            disabled={busy || confirm.trim().toLowerCase() !== email.toLowerCase()}>
            {busy ? "Deleting…" : "Delete account"}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────── small atoms ─────────────────────── */

const TabBtn = ({
  id, active, setTab, children,
}: {
  id: Tab; active: Tab; setTab: (t: Tab) => void; children: React.ReactNode;
}) => (
  <button type="button" onClick={() => setTab(id)} className={active === id ? "on" : ""}>
    {children}
  </button>
);

const NotifRow = ({
  title, meta, on, busy, onFlip,
}: {
  title: string; meta: string; on: boolean; busy: boolean; onFlip: () => void;
}) => (
  <div className="profile-row">
    <div style={{ flex: 1 }}>
      <div className="v">{title}</div>
      <div className="meta">{meta}</div>
    </div>
    <button type="button" aria-pressed={on} aria-label={`Toggle ${title}`}
      onClick={onFlip} disabled={busy}
      className={`ui-toggle${on ? " on" : ""}`}
    />
  </div>
);

const FieldNote = ({ tone, children }: { tone: "ok" | "err"; children: React.ReactNode }) => (
  <div className="field-help" style={{
    color: tone === "err" ? "rgb(var(--wo-danger))" : "rgb(var(--wo-teal))",
    marginTop: 8,
  }}>{children}</div>
);

/* ─────────────────────── helpers ─────────────────────── */

const summariseUA = (ua: string | null): string => {
  if (!ua) return "Unknown device";
  // Lightweight UA summary — full parsing isn't worth the bytes.
  const browser =
    /Chrome\/[\d.]+/.test(ua) && !/Edg\//.test(ua) ? "Chrome" :
    /Edg\//.test(ua)                              ? "Edge" :
    /Firefox\//.test(ua)                          ? "Firefox" :
    /Safari\//.test(ua)                           ? "Safari" :
                                                    "Browser";
  const os =
    /Mac OS X/.test(ua)  ? "macOS" :
    /Windows/.test(ua)   ? "Windows" :
    /iPhone|iPad/.test(ua) ? "iOS" :
    /Android/.test(ua)   ? "Android" :
    /Linux/.test(ua)     ? "Linux" :
                           "device";
  return `${browser} · ${os}`;
};

const timeAgo = (iso: string): string => {
  const diffMs = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diffMs / 60_000);
  if (m < 1)   return "just now";
  if (m < 60)  return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24)  return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30)  return `${d}d ago`;
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const formatBytes = (n: number): string =>
  n < 1024 ? `${n} B` : n < 1024 * 1024 ? `${(n / 1024).toFixed(1)} KB` : `${(n / 1024 / 1024).toFixed(1)} MB`;

const downloadJsonFromBase64 = (b64: string, filename: string) => {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
  const blob = new Blob([bytes], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};
